/**
 * Form 1120 line 19 (charitable contributions) and line 29a (the NOL deduction)
 * feed each other, so one function decides both. The API Worker's return model
 * and the customer app's Summary preview call it, so they can never disagree.
 *
 * Sources:
 * - Instructions for Form 1120 (2025), line 19: the 10% limit "is applied using
 *   the taxable income after taking into account any deduction for the net
 *   operating loss" carryover. IRC 170(b)(2)(D) leaves out only an NOL
 *   carryback, so a carryover reduces the 10% base (CCA 201928014).
 * - IRC 172(a)(2): a post-2017 NOL deduction is at most 80% of taxable income
 *   figured without it, and after the charitable deduction. That is circular:
 *   each limit's base depends on the other deduction.
 * - T.D. 9905 (preamble): Treasury lets a taxpayer use any reasonable approach,
 *   including simultaneous equations, applied consistently. This is the
 *   simultaneous solution, in whole dollars, rounded down.
 *
 * Special deductions (line 29b) are always 0 in our model, so the "taxable
 * income" both limits start from is line 11 less lines 12 through 26.
 */

export interface CharitableAndNolInput {
  /**
   * Line 11 less lines 12 through 26: income before the charitable deduction
   * (line 19), before the NOL deduction and before special deductions. Whole
   * dollars; negative for a loss.
   */
  incomeBeforeCharity: number;
  /** Charitable contributions paid this year, whole dollars. */
  contributions: number;
  /** The post-2017 NOL carryover available (Schedule K item 12), whole dollars. */
  nolCarryover: number;
}

export interface CharitableAndNolResult {
  /** Line 19: the allowed charitable deduction. The rest carries over. */
  charitable: number;
  /** Line 29a: the allowed NOL deduction. */
  nol: number;
}

/**
 * The most that line 19 and line 29a can be, given that the 10% limit is on
 * income after the NOL and the 80% limit is on income after the gift.
 *
 * With T, K and Q for the three inputs, C and N for the answers:
 *   C = min(K, 10% of (T - N))   and   N = min(Q, 80% of (T - C)).
 * Which limits bind decides the case (all in exact integer arithmetic):
 *   1. The whole gift is allowed: C = K, N = min(Q, 80% of (T - K)); good when
 *      K is at most 10% of (T - N).
 *   2. Only the 10% limit binds and the NOL is used in full: N = Q,
 *      C = min(K, 10% of (T - Q)); good when Q is at most 80% of (T - C).
 *   3. Both bind: C = 2% of T / 92% and N = 72% of T / 92%, i.e. T/46 and 18T/23.
 * A loss year allows neither.
 */
export function charitableAndNolDeductions(
  input: CharitableAndNolInput,
): CharitableAndNolResult {
  const t = input.incomeBeforeCharity;
  const k = Math.max(0, input.contributions);
  const q = Math.max(0, input.nolCarryover);
  if (t <= 0) return { charitable: 0, nol: 0 };

  const nolAfterFullGift = Math.max(
    0,
    Math.min(q, Math.floor((4 * (t - k)) / 5)),
  );
  if (10 * k <= t - nolAfterFullGift) {
    return { charitable: k, nol: nolAfterFullGift };
  }

  const giftAfterFullNol = Math.max(0, Math.min(k, Math.floor((t - q) / 10)));
  if (5 * q <= 4 * (t - giftAfterFullNol)) {
    return { charitable: giftAfterFullNol, nol: q };
  }

  return { charitable: Math.floor(t / 46), nol: Math.floor((18 * t) / 23) };
}
